import { gatewayApi as api } from './gatewayApi';

// Types
export interface Part {
  id?: string;
  part_num: string;
  description: string;
  part_issue: string;
  client_id: string;
  part_classification_id: string;
  price_per_unit: number;
  is_active: boolean;
  created_by?: string;
  modified_by?: string;
  part_status_id?: string;
  approved_by?: string;
  created_date?: string;
  modified_date?: string;
  obsolete_date?: string | null;
  // Nested objects
  status?: {
    status_id: string;
    status_name: string;
    status_code: string;
    description: string;
    created_date: string;
    modified_date: string;
    obsolete_date: string | null;
    is_active: boolean;
  } | null;
  client?: {
    client_id: string;
    name: string;
    description: string;
    created_date: string;
    modified_date: string;
    obsolete_date: string | null;
    is_active: boolean;
  } | null;
  creator?: {
    user_id: string;
    role_id: string;
    first_name: string;
    last_name: string;
    employee_number: string | null;
    email: string;
    is_supervisor: boolean;
    department_id: string;
    user_image: string | null;
    created_date: string;
    modified_date: string;
    obsolete_date: string | null;
    is_active: boolean;
  } | null;
  Documents?: Document[];
  PartSerials?: Serial[];
}

export interface Document {
  id: string;
  Name: string;
  DocumentNum: string;
  Description: string;
  Version: string;
}

export interface Serial {
  id: string;
  SerialNum: string;
  assigned: boolean;
}

export interface PartsResponse {
  data: Part[];
  total: number;
  page: number;
  limit: number;
}

// Transform frontend data to backend format
const transformToBackendFormat = (part: Part) => ({
  Number: part.part_num,
  Description: part.description,
  ClientId: parseInt(part.client_id),
  Issue: part.part_issue,
  PricePerUnit: part.price_per_unit,
  Classification: parseInt(part.part_classification_id),
  serialData: part.PartSerials?.map(serial => serial.SerialNum) || [],
  documentData: part.Documents?.map(doc => ({
    Name: doc.Name,
    DocumentNum: doc.DocumentNum,
    Description: doc.Description,
    Version: doc.Version
  })) || []
});

// Transform backend data to frontend format
const transformFromBackendFormat = (backendPart: any): Part => ({
  id: backendPart.Id?.toString(),
  part_num: backendPart.Number,
  description: backendPart.Description,
  part_issue: backendPart.Issue,
  client_id: backendPart.ClientId?.toString(),
  part_classification_id: backendPart.Classification?.toString(),
  price_per_unit: backendPart.PricePerUnit,
  is_active: backendPart.Active,
  created_by: backendPart.CreatedBy?.toString(),
  modified_by: backendPart.ModifiedBy?.toString(),
  part_status_id: backendPart.PartStatusId?.toString(),
  approved_by: backendPart.ApprovedBy?.toString(),
  created_date: backendPart.CreatedDate,
  modified_date: backendPart.ModifiedDate,
  obsolete_date: backendPart.ObsoleteDate,
  status: backendPart.Status ? {
    status_id: backendPart.Status.Id?.toString(),
    status_name: backendPart.Status.StatusName,
    status_code: backendPart.Status.StatusCode,
    description: backendPart.Status.Description,
    created_date: backendPart.Status.CreatedDate,
    modified_date: backendPart.Status.ModifiedDate,
    obsolete_date: backendPart.Status.ObsoleteDate,
    is_active: backendPart.Status.Active
  } : null,
  client: backendPart.Client ? {
    client_id: backendPart.Client.Id?.toString(),
    name: backendPart.Client.Name,
    description: backendPart.Client.Description,
    created_date: backendPart.Client.CreatedDate,
    modified_date: backendPart.Client.ModifiedDate,
    obsolete_date: backendPart.Client.ObsoleteDate,
    is_active: backendPart.Client.Active
  } : null,
  creator: backendPart.Creator ? {
    user_id: backendPart.Creator.Id?.toString(),
    role_id: backendPart.Creator.RoleId?.toString(),
    first_name: backendPart.Creator.FirstName,
    last_name: backendPart.Creator.LastName,
    employee_number: backendPart.Creator.EmployeeNumber,
    email: backendPart.Creator.Email,
    is_supervisor: backendPart.Creator.IsSupervisor,
    department_id: backendPart.Creator.DepartmentId?.toString(),
    user_image: backendPart.Creator.UserImage,
    created_date: backendPart.Creator.CreatedDate,
    modified_date: backendPart.Creator.ModifiedDate,
    obsolete_date: backendPart.Creator.ObsoleteDate,
    is_active: backendPart.Creator.Active
  } : null,
  Documents: backendPart.Documents || [],
  PartSerials: backendPart.PartSerials || []
});

// Parts API Service
export const partsApi = {
  // Get all parts
  getAll: (params?: { page?: number; limit?: number; search?: string }): Promise<PartsResponse> =>
    api.get<PartsResponse>('/part', params).then(response => ({
      ...response,
      data: response.data?.map(transformFromBackendFormat) || []
    })),

  // Get part by ID
  getById: (id: string): Promise<Part> =>
    api.get<{ data: any }>(`/part/${id}`).then(response => transformFromBackendFormat(response.data)),

  // Create new part
  create: (part: Omit<Part, 'id'>): Promise<Part> =>
    api.post<{ data: any }>('/part', { data: transformToBackendFormat(part) }).then(response => transformFromBackendFormat(response.data)),

  // Update part
  update: (id: string, part: Partial<Part>): Promise<Part> =>
    api.put<{ data: any }>('/part', { 
      Id: parseInt(id), 
      data: transformToBackendFormat(part as Part) 
    }).then(response => transformFromBackendFormat(response.data)),

  // Delete part
  delete: (id: string): Promise<void> =>
    api.delete<void>(`/part/${id}`),

  // Search parts
  search: (field: string, value: string): Promise<Part[]> =>
    api.get<Part[]>(`/part/search/${field}/${value}`),

  // Get part documents
  getDocuments: (partId: string): Promise<Document[]> =>
    api.get<Document[]>(`/part/${partId}/documents`),

  // Upload document
  uploadDocument: (partId: string, formData: FormData): Promise<Document> =>
    api.upload<Document>(`/part/${partId}/documents`, formData),

  // Delete document
  deleteDocument: (partId: string, documentId: string): Promise<void> =>
    api.delete<void>(`/part/${partId}/documents/${documentId}`),

  // Get part serials
  getSerials: (partId: string): Promise<Serial[]> =>
    api.get<Serial[]>(`/part/${partId}/serials`),

  // Add serial
  addSerial: (partId: string, serial: Omit<Serial, 'id'>): Promise<Serial> =>
    api.post<Serial>(`/part/${partId}/serials`, serial),

  // Delete serial
  deleteSerial: (partId: string, serialId: string): Promise<void> =>
    api.delete<void>(`/part/serial/${serialId}`),

  // Import serials
  importSerials: (partId: string, formData: FormData): Promise<Serial[]> =>
    api.upload<Serial[]>(`/part/${partId}/serials/import`, formData),

  // Approve part
  approve: (id: string, comment?: string): Promise<Part> =>
    api.put<Part>('/part/approve', { id, comment }),

  // Reject part
  reject: (id: string, comment?: string): Promise<Part> =>
    api.put<Part>('/part/reject', { id, comment }),

  // Get part status
  getStatus: (id: string): Promise<{ status: string; comment?: string }> =>
    api.get<{ status: string; comment?: string }>(`/part/${id}/status`),

  // Stored Procedures and Custom Endpoints
  getPartPOBySerials: (serials: string[]): Promise<{ data: any[] }> =>
    api.post('/part/proc/getPartPOBySerials', { Serials: serials }),

  getPartStatusPossibilities: (itemType: string, itemKey: string): Promise<{ data: any[] }> =>
    api.get('/part/getPartStatusPossibilities', { ItemType: itemType, Item_Key: itemKey }),

  getOperationPartLog: (): Promise<{ data: any[] }> =>
    api.get('/part/operationpartLog'),

  // Additional custom endpoints for planning/reports
  getOperationTP: (): Promise<any[]> =>
    api.get('/part/operationTP'),

  getOperationNC: (): Promise<any[]> =>
    api.get('/part/operationNC'),

  getPartLog: (): Promise<any[]> =>
    api.get('/part/partLog'),

  getPartLogF: (): Promise<any[]> =>
    api.get('/part/partLogF'),

  getShipPerWeek: (): Promise<any[]> =>
    api.get('/part/shipPerWeek'),

  getWoPerPart: (): Promise<any[]> =>
    api.get('/part/woPerPart'),

  getOperationDelay: (): Promise<any[]> =>
    api.get('/part/operationDelay'),
}; 